import React, { useState, useMemo } from 'react';
import { Application } from '../../types';

interface ApplicationsTableProps {
  applications: Application[];
  onEditApplication?: (application: Application) => void;
  onViewNotes?: (notes: string | null, title: string) => void;
}

type SortField = 'name' | 'usage' | 'location' | 'tier';
type SortDirection = 'asc' | 'desc';

export const ApplicationsTable: React.FC<ApplicationsTableProps> = ({
  applications,
  onEditApplication,
  onViewNotes
}) => {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      // Handle undefined values
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      
      // Compare based on direction
      if (sortDirection === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [applications, sortField, sortDirection]);

  // Helper to render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 12L6 17L11 12M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 12L6 17L11 12" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  return (
    <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-transparent border">
        <thead>
                  <tr className="bg-transparent/5">
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">
              <div 
                className="flex justify-between items-center cursor-pointer" 
                onClick={() => handleSort('name')}
              >
                Application Name
                {renderSortIndicator('name')}
              </div>
            </th>
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">
              <div 
                className="flex justify-between items-center cursor-pointer" 
                onClick={() => handleSort('usage')}
              >
                Usage
                {renderSortIndicator('usage')}
              </div>
            </th>
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">
              <div 
                className="flex justify-between items-center cursor-pointer" 
                onClick={() => handleSort('location')}
              >
                Location
                {renderSortIndicator('location')}
              </div>
            </th>
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">
              <div 
                className="flex justify-between items-center cursor-pointer" 
                onClick={() => handleSort('tier')}
              >
                Tier
                {renderSortIndicator('tier')}
              </div>
            </th>
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">Customer Facing Notes</th>
            <th className="text-left p-3 border-b border-gray-200 font-medium text-[#0F2A4F]">Internal Notes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedApplications.map((app, index) => (
            <tr 
              key={app.id}
                  className={`border-b border-gray-200 bg-transparent text-sm`}
            >
              <td className="p-3 text-[#0F2A4F]">{app.name}</td>
              <td className="p-3 text-[#0F2A4F]">{app.usage}</td>
              <td className="p-3 text-[#0F2A4F] italic">{app.location}</td>
              <td className="p-3 text-[#0F2A4F]">{app.tier}</td>
              <td className="p-3 text-[#0F2A4F] max-w-md">
                <div 
                  className="text-sm truncate cursor-pointer inter-regular"
                  onClick={() => onViewNotes && onViewNotes(app.customer_facing_notes, 'Customer Facing Notes')}
                >
                  {app.customer_facing_notes ? app.customer_facing_notes : '--'}
                </div>
              </td>
              <td className="p-3 text-[#0F2A4F] max-w-md">
                <div 
                  className="text-sm truncate cursor-pointer inter-regular"
                  onClick={() => onViewNotes && onViewNotes(app.internal_notes, 'Internal Notes')}
                >
                  {app.internal_notes ? app.internal_notes : '--'}
                </div>
              </td>
                  <td>
                      <button 
                          className='p-2 rounded-full hover:cursor-pointer hover:bg-transparent/5'
                          onClick={() => onEditApplication && onEditApplication(app)}
                      >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 3.00023H5C4.46957 3.00023 3.96086 3.21094 3.58579 3.58601C3.21071 3.96109 3 4.46979 3 5.00023V19.0002C3 19.5307 3.21071 20.0394 3.58579 20.4144C3.96086 20.7895 4.46957 21.0002 5 21.0002H19C19.5304 21.0002 20.0391 20.7895 20.4142 20.4144C20.7893 20.0394 21 19.5307 21 19.0002V12.0002M18.375 2.62523C18.7728 2.2274 19.3124 2.00391 19.875 2.00391C20.4376 2.00391 20.9772 2.2274 21.375 2.62523C21.7728 3.02305 21.9963 3.56262 21.9963 4.12523C21.9963 4.68784 21.7728 5.2274 21.375 5.62523L12.362 14.6392C12.1245 14.8765 11.8312 15.0501 11.509 15.1442L8.636 15.9842C8.54995 16.0093 8.45874 16.0108 8.37191 15.9886C8.28508 15.9663 8.20583 15.9212 8.14245 15.8578C8.07907 15.7944 8.03389 15.7151 8.01164 15.6283C7.9894 15.5415 7.9909 15.4503 8.016 15.3642L8.856 12.4912C8.95053 12.1693 9.12453 11.8763 9.362 11.6392L18.375 2.62523Z" stroke="#062E5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                      </button>
                  </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
